import { BaseText, ListItem, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import { LoyaltyLogo } from 'components/services/LoyaltyLogo';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { CompanyBenefit } from 'types/generatedSchemaTypes';

export const benefitLabelMap: Record<string, string> = {
  USE_CORPORATE_ACCOUNT: 'account.benefit.corporateAccount',
  USE_LOYALTY_PMC: 'account.benefit.loyaltyMembership',
  USE_MEMBER_GROUP_LTP: 'account.benefit.memberGroup',
  USE_PMC: 'account.benefit.pmc',
  USE_PMC_AUTOMATIC: 'account.benefit.pmcAutomatic',
  USE_POOL: 'account.benefit.poolBenefit',
};

const getBenefitLabel = (benefitTargetType: string): string =>
  benefitLabelMap[benefitTargetType] ?? 'account.benefit.parkingBenefit';

const getBenefitIconName = (benefitTargetType: string): string => {
  if (benefitTargetType === 'USE_CORPORATE_ACCOUNT') {
    return 'Business-Outlined';
  }
  // TODO: Need to confirm the logo in used for other target types
  return 'Service-Filled';
};

const IconComponent = (benefit: CompanyBenefit) =>
  benefit.logo ? (
    <LoyaltyLogo uri={benefit.logo} width="48px" height="48px" />
  ) : (
    <View style={styles.iconCircle}>
      <Icon size="m" name={getBenefitIconName(benefit.benefitTargetType)} />
    </View>
  );

const TitleComponent = (
  benefit: CompanyBenefit,
  label: string,
  index: number
) => {
  return (
    <View>
      <Meta color={theme.colors.gray300}>{label}</Meta>
      <BaseText
        numberOfLines={1}
        testID={`benefit-name-${index}`}
        style={styles.benefitName}>
        {benefit.name}
      </BaseText>
    </View>
  );
};

type BenefitCardProps = {
  benefit: CompanyBenefit;
  index: number;
  onPress?: (benefit: CompanyBenefit) => void;
};

const BenefitCard: React.FC<BenefitCardProps> = ({
  benefit,
  index,
  onPress,
}) => {
  const { t } = useTranslation();

  return (
    <View style={styles.cardWrapper}>
      <ListItem
        item={benefit}
        indexValue={index}
        onPress={onPress ? () => onPress(benefit) : undefined}
        iconComponent={() => IconComponent(benefit)}
        titleComponent={() =>
          TitleComponent(
            benefit,
            t(getBenefitLabel(benefit.benefitTargetType)),
            index
          )
        }
        testID={`benefit-card-${index}`}
        paddingVertical={16}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    borderRadius: 24,
    borderWidth: 1,
    borderColor: theme.colors.gray100,
    overflow: 'hidden',
    marginVertical: 6,
  },
  benefitName: {
    fontWeight: 'bold',
  },
  iconCircle: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: theme.colors.lightBg.green,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default BenefitCard;
